import { FunctionComponent, useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { UserContext } from '../../context';
import {
  BackButton,
  isPushNotificationsAllowed,
  layoutPath,
  Switch,
  useIsMyTopicsAllowed,
} from '../../shared';
import { AchievementsSection } from './AchievementsSection';
import { ActivitySection } from './ActivitySection';
import { ChannelsSection } from './ChannelsSection';
import { TagsSection } from './TagsSection';
import { usePushPermission } from './usePushPermission';

import classes from './PushNotifications.module.scss';

export const PushNotifications: FunctionComponent = () => {
  const { t } = useTranslation();

  const { t: ctxT } = useTranslation('', { keyPrefix: 'pushNotifications' });

  const navigate = useNavigate();

  const { isAnonymous } = useContext(UserContext).userProfile;

  const { isPermissionGranted, requestPermission } = usePushPermission();

  const isMyTopicsAllowed = useIsMyTopicsAllowed();

  const sections = useMemo(() => {
    if (!isPermissionGranted) {
      return (
        <>
          <div className={classes['push-notifications__hint']}>{ctxT('permission-off-hint')}</div>
          {!isAnonymous && <ActivitySection isPushLocked />}
          {!isAnonymous && <AchievementsSection isPushLocked />}
        </>
      );
    }

    if (isAnonymous) {
      return null;
    }

    return (
      <>
        {isMyTopicsAllowed && (
          <div className={classes['push-notifications__banner']}>
            <Trans
              i18nKey={'pushNotifications.scope-banner'}
              values={{ screenName: t('settings.my-topics') }}
            >
              <Link
                className={classes['push-notifications__banner-link']}
                to={layoutPath('/myTopics')}
              ></Link>
            </Trans>
          </div>
        )}

        <ChannelsSection />

        <TagsSection />

        <ActivitySection />

        <AchievementsSection />
      </>
    );
  }, [ctxT, isAnonymous, isMyTopicsAllowed, isPermissionGranted, t]);

  if (!isPushNotificationsAllowed) {
    navigate('/notFound');
    return null;
  }

  return (
    <div className={classes['push-notifications']}>
      <BackButton />

      <div>
        <h1 className={classes['push-notifications__title']}>{t('settings.push-notifications')}</h1>
        <div className={classes['push-notifications__sub-title']}>{ctxT('sub-title')}</div>
      </div>

      <div className={classes['push-notifications__main']}>
        <Switch
          label={t('common.notification')}
          className={classes['push-notifications__main-switch']}
          checked={isPermissionGranted}
          onChange={requestPermission}
        />
      </div>

      {sections}
    </div>
  );
};
